import React, { useMemo } from 'react';
import PropTypes from "prop-types";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import moment from "moment";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Card from "@mui/material/Card";

/**
 * Combined Chart Component - Dùng Recharts để hiển thị Orders, Revenue, Basecost trong 1 chart
 */
function CombinedChart({ title = "Dashboard Overview", ordersData = [], revenueData = [], basecostData = [], height = "400px" }) {
  // Kết hợp data thành format cho Recharts
  const chartData = useMemo(() => {
    // Lấy tất cả labels (giả định tất cả có cùng labels)
    const labels = ordersData.map(d => d.label || d.date || '');
    
    return labels.map((label, index) => {
      const ordersValue = ordersData[index]?.value || 0;
      const revenueValue = revenueData[index]?.value || 0;
      const basecostValue = basecostData[index]?.value || 0;
      
      // Format label date - Format theo ngày
      let formattedLabel = label;
      if (label) {
        // Kiểm tra nếu là timestamp (milliseconds) từ moment().format("x")
        if (typeof label === 'string' && /^\d+$/.test(label) && label.length >= 10) {
          // Parse timestamp và format bằng moment
          formattedLabel = moment(parseInt(label)).format('MM/DD');
        } else {
          // Thử parse như date string
          const date = new Date(label);
          if (!isNaN(date.getTime())) {
            formattedLabel = moment(date).format('MM/DD');
          } else if (label.length > 10) {
            formattedLabel = label.substring(0, 10);
          }
        }
      }
      
      return {
        name: formattedLabel,
        Orders: ordersValue,
        Revenue: revenueValue,
        Basecost: basecostValue,
      };
    });
  }, [ordersData, revenueData, basecostData]);

  // Format giá trị lớn
  const formatValue = (value) => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}k`;
    }
    return value;
  };

  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <MDBox p={2} sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <MDTypography variant="h6" textAlign="center" mb={2}>
          {title}
        </MDTypography>
        <MDBox
          sx={{
            width: '100%',
            flex: 1,
            minHeight: height === "100%" ? '400px' : height,
            position: 'relative',
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{
                top: 10,
                right: 10,
                left: 0,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 12, fill: '#666' }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: '#666' }}
                tickFormatter={formatValue}
              />
              <Tooltip 
                formatter={(value, name) => [formatValue(value), name]}
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                }}
              />
              <Legend 
                wrapperStyle={{ paddingTop: '10px' }}
                iconType="line"
              />
              
              {/* Orders Bar - Màu xanh */}
              <Bar 
                dataKey="Orders" 
                fill="#1976d2" 
                name="Orders"
                radius={[4, 4, 0, 0]}
              />
              
              {/* Revenue Line - Màu xanh lá, mỏng */}
              <Line 
                type="monotone" 
                dataKey="Revenue" 
                stroke="#2e7d32" 
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 5, fill: '#2e7d32', stroke: '#fff', strokeWidth: 2 }}
                name="Revenue"
              />
              
              {/* Basecost Bar - Màu cam */}
              <Bar 
                dataKey="Basecost" 
                fill="#ed6c02" 
                name="Basecost"
                radius={[4, 4, 0, 0]}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </MDBox>
      </MDBox>
    </Card>
  );
}

// PropTypes validation
CombinedChart.propTypes = {
  title: PropTypes.string,
  ordersData: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      date: PropTypes.string,
      value: PropTypes.number.isRequired,
    })
  ),
  revenueData: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      date: PropTypes.string,
      value: PropTypes.number.isRequired,
    })
  ),
  basecostData: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      date: PropTypes.string,
      value: PropTypes.number.isRequired,
    })
  ),
  height: PropTypes.string,
};

CombinedChart.defaultProps = {
  title: "Dashboard Overview",
  ordersData: [],
  revenueData: [],
  basecostData: [],
  height: "400px",
};

export default CombinedChart;
